import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Typography,
  Switch,
  IconButton,
  Divider
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { updateBankFormField } from '../../../../modules/masterdata/actions';
import {
  selectUpdateBankFormFieldLoading,
  selectUpdateBankFormFieldSuccess,
  selectUpdateBankFormFieldError
} from '../../../../modules/masterdata/selectors';
import { resetUpdateBankFormFieldState } from '../../../../modules/masterdata/reducers';
import { IBankForm } from '../../../../modules/masterdata/types';
import { AddCircleOutline, Delete } from '@mui/icons-material';
import ResponsiveSelect from '../../../../components/ResponsiveSelect';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';

interface EditBankFormModalProps {
  open: boolean;
  onClose: () => void;
  fieldData: IBankForm | null;
}

const FIELD_TYPE_CHOICES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'radio', label: 'Radio' },
  { value: 'checkbox', label: 'Checkbox' },
];

const EditBankFormModal: React.FC<EditBankFormModalProps> = ({ open, onClose, fieldData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(selectUpdateBankFormFieldLoading);
  const success = useSelector(selectUpdateBankFormFieldSuccess);
  const error = useSelector(selectUpdateBankFormFieldError);

  const [fieldName, setFieldName] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [fieldType, setFieldType] = useState('text');
  const [fieldOptions, setFieldOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [errors, setErrors] = useState<{ fieldName?: string }>({});

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (fieldData) {
      setFieldName(fieldData.field_name);
      setIsRequired(fieldData.is_required);
      setFieldType(fieldData.field_type);
      setFieldOptions(fieldData.field_options || []);
    }
  }, [fieldData, open]);

  const handleClose = useCallback(() => {
    setErrors({});
    dispatch(resetUpdateBankFormFieldState());
    onClose();
  }, [dispatch, onClose]);

  useEffect(() => {
    if (success) {
      handleClose();
    }
  }, [success, handleClose]);

  const validate = () => {
    const newErrors: { fieldName?: string } = {};
    if (!fieldName.trim()) newErrors.fieldName = 'Field Name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate() && fieldData) {
      dispatch(updateBankFormField({
        field_id: fieldData.field_id,
        field_name: fieldName,
        field_type: fieldType,
        is_required: isRequired,
        field_options: fieldOptions,
      }));
    }
  };

  const handleAddOption = () => {
    if (newOption.trim() && !fieldOptions.includes(newOption.trim())) {
      setFieldOptions([...fieldOptions, newOption.trim()]);
      setNewOption('');
    }
  };

  const handleDeleteClick = (option: string) => {
    setOptionToDelete(option);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (optionToDelete) {
      setFieldOptions(fieldOptions.filter(o => o !== optionToDelete));
    }
    setIsDeleteDialogOpen(false);
    setOptionToDelete(null);
  };

  const hasOptions = ['dropdown', 'radio', 'checkbox'].includes(fieldType);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Bank Form Field</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Field Name"
            variant="outlined"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            error={!!errors.fieldName}
            helperText={errors.fieldName}
          />

          <ResponsiveSelect
            label="Field Type"
            value={fieldType}
            onChange={(val) => setFieldType(val)}
            options={FIELD_TYPE_CHOICES}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#5E8E7E',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#5E8E7E',
                  },
                }}
              />
            }
            label="Is Required"
          />

          {hasOptions && (
            <Box sx={{ border: '1px solid #eee', p: 2, borderRadius: 1 }}>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                Field Options
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Option value"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddOption}
                  startIcon={<AddCircleOutline />}
                  sx={{
                    bgcolor: '#5E8E7E',
                    color: 'white',
                    '&:hover': { bgcolor: '#4A7A6A' },
                    textTransform: 'none',
                    px: 2
                  }}
                >
                  ADD
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {fieldOptions.length > 0 ? (
                  fieldOptions.map((option, index) => (
                    <React.Fragment key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                        <Typography sx={{ color: '#333' }}>{option}</Typography>
                        <IconButton size="small" onClick={() => handleDeleteClick(option)} sx={{ color: '#E57373' }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                      {index < fieldOptions.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <Typography variant="body2" color="textDisabled">No options available</Typography>
                )}
              </Box>
            </Box>
          )}

          {error && (
            <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
              {error}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: '#5E8E7E', '&:hover': { bgcolor: '#4A7A6A' }, px: 3 }}
        >
          {loading ? 'Updating...' : 'UPDATE FIELD'}
        </Button>
      </DialogActions>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Remove Option"
        message={`Are you sure you want to remove the option "${optionToDelete}"?`}
      />
    </Dialog>
  );
};

export default EditBankFormModal;

